import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";

const getUniqueSchools = (assignmentGroup) => {
  const schoolsById = new Map();
  (assignmentGroup?.assignments || []).forEach((assignment) => {
    const school = assignment?.schoolClass?.school;
    if (school?.id && !schoolsById.has(school.id)) {
      schoolsById.set(school.id, school);
    }
  });
  return Array.from(schoolsById.values()).sort((a, b) =>
    (a.name || "").localeCompare(b.name || ""),
  );
};

export const ExportGroupResultsDialog = ({
  open,
  setOpen,
  assignmentGroup,
  onConfirm,
}) => {
  const schools = useMemo(
    () => getUniqueSchools(assignmentGroup),
    [assignmentGroup],
  );
  const [selectedSchoolIds, setSelectedSchoolIds] = useState([]);

  useEffect(() => {
    if (open) {
      // Default to all schools selected
      setSelectedSchoolIds(schools.map((s) => s.id));
    }
  }, [open, schools]);

  const allSelected =
    schools.length > 0 && selectedSchoolIds.length === schools.length;

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggleSchool = (schoolId) => {
    setSelectedSchoolIds((prev) =>
      prev.includes(schoolId)
        ? prev.filter((id) => id !== schoolId)
        : [...prev, schoolId],
    );
  };

  const handleToggleSelectAll = () => {
    setSelectedSchoolIds(allSelected ? [] : schools.map((s) => s.id));
  };

  const handleConfirm = () => {
    // If every school is selected, send undefined so the backend exports the
    // whole group (keeps behaviour identical to the pre-filter export).
    onConfirm(selectedSchoolIds);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      data-testid="export-group-results-dialog"
    >
      <DialogTitle>Export results</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }} color="text.secondary">
          Select which schools to include in the exported results.
        </Typography>

        {schools.length === 0 ? (
          <Typography>No schools available to export.</Typography>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedSchoolIds.length > 0 && !allSelected}
                  onChange={handleToggleSelectAll}
                />
              }
              label="Select all"
            />
            <Divider sx={{ my: 1 }} />
            <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
              <Stack sx={{ ml: 2 }}>
                {schools.map((school) => (
                  <FormControlLabel
                    key={school.id}
                    control={
                      <Checkbox
                        checked={selectedSchoolIds.includes(school.id)}
                        onChange={() => handleToggleSchool(school.id)}
                      />
                    }
                    label={school.name}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={selectedSchoolIds.length === 0}
          >
            Export
            {selectedSchoolIds.length > 0 &&
            selectedSchoolIds.length < schools.length
              ? ` (${selectedSchoolIds.length} school${
                  selectedSchoolIds.length !== 1 ? "s" : ""
                })`
              : ""}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
